"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import NextLink from "next/link";
import {
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Input,
  InputGroup,
  Stack,
  Text,
  Heading,
  Flex,
} from "@chakra-ui/react";
import { LuArrowRight } from "react-icons/lu";
import { LuSearch } from "react-icons/lu";
import type { BlogPost } from "@/app/models/blog.models";
import { toPlainText } from "@/app/lib/blog-text";
import { getBlogCoverImageUrl } from "@/app/lib/blog-media";
import { useLoading } from "@/app/context/LoadingProvider";

type BlogListProps = {
  posts: BlogPost[];
};

const formatPublishDate = (date: string) =>
  new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
  }).format(new Date(date));

const getContentPreview = (content: string, maxLength = 140) => {
  const plainText = toPlainText(content);
  if (plainText.length <= maxLength) return plainText;
  return `${plainText.slice(0, maxLength).trim()}...`;
};

export function BlogList({ posts }: BlogListProps) {
  const [query, setQuery] = useState("");
  const { setPageLoading } = useLoading();

  const filteredPosts = useMemo(() => {
    const term = query.trim().toLowerCase();

    return posts.filter((post) => {
      if (!term) return true;

      const titleMatch = post.title.toLowerCase().includes(term);
      const categoryTextMatch = post.category.toLowerCase().includes(term);
      const tagsMatch = (post.tags ?? [])
        .map((tag) => tag.toLowerCase())
        .some((tag) => tag.includes(term));

      return titleMatch || categoryTextMatch || tagsMatch;
    });
  }, [posts, query]);

  return (
    <Stack gap={{ base: 5, md: 6 }}>
      <Flex
        direction={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap={3}
      >
        <Stack gap={0.5}>
          <Heading as="h3" fontSize={{ base: "2xl", md: "3xl" }}>
            Explore Articles
          </Heading>
          <Text fontSize={{ base: "sm", md: "md" }} color="fg.muted">
            Discover practical Bengali guides, stories, and learning tips.
          </Text>
        </Stack>
        <Flex justify={{ base: "flex-end", md: "flex-end" }} align="center" gap={2}>
          <InputGroup startElement={<LuSearch />} maxW={{ base: "100%", md: "360px" }}>
            <Input
              borderColor="teal"
              placeholder="Search title, category, or tags"
              size="sm"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </InputGroup>
        </Flex>
      </Flex>

      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={{ base: 5, md: 6 }}>
        {filteredPosts.map((post) => {
          const imageUrl = getBlogCoverImageUrl(post.slug);

          return (
            <Card.Root
              key={post.slug}
              overflow="hidden"
              rounded="2xl"
              border="1px solid"
              borderColor="blackAlpha.200"
              bgGradient="linear(150deg, rgba(56, 189, 248, 0.12), rgba(14, 165, 233, 0.08) 50%, rgba(249, 115, 22, 0.12))"
              _dark={{
                borderColor: "whiteAlpha.300",
                bgGradient:
                  "linear(150deg, rgba(56, 189, 248, 0.12), rgba(168, 85, 247, 0.12) 50%, rgba(251, 146, 60, 0.14))",
              }}
              h="100%"
              display="flex"
              flexDirection="column"
              transition="transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease"
              _hover={{
                transform: "translateY(-6px)",
                boxShadow: "0 18px 38px rgba(0, 0, 0, 0.35)",
                borderColor: "teal.300",
              }}
            >
                <Box position="relative" h="200px" overflow="hidden">
                  <Image
                    src={imageUrl}
                    alt={post.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    style={{ objectFit: "cover" }}
                  />
                  <Box
                    position="absolute"
                    inset={0}
                    bgGradient="linear(to-t, rgba(0,0,0,0.52), rgba(0,0,0,0.08))"
                  />
                </Box>
                <Card.Body gap={4} display="flex" flexDirection="column" flex="1">
                  <Stack gap={2} flex="1">
                    <Badge colorPalette="orange" variant="subtle" alignSelf="flex-start" display="inline-flex">
                      {formatPublishDate(post.publishedAt)}
                    </Badge>
                    <Heading
                      as="h2"
                      fontSize="xl"
                      lineHeight="1.35"
                      minH="3.2em"
                      css={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {post.title}
                    </Heading>
                    <Text
                      fontSize="sm"
                      lineHeight="1.7"
                      color="fg.muted"
                      minH="4.8em"
                      css={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {getContentPreview(post.content)}
                    </Text>
                  </Stack>
                  <Button
                    asChild
                    mt="auto"
                    pt={1}
                    size="sm"
                    variant="ghost"
                    alignSelf="flex-start"
                    rounded="full"
                    color="teal.700"
                    _dark={{ color: "cyan.200", _hover: { bg: "whiteAlpha.200" } }}
                    fontWeight="semibold"
                    _hover={{ textDecoration: "none", bg: "blackAlpha.100" }}
                    _focusVisible={{
                      outline: "2px solid",
                      outlineColor: "teal.400",
                      outlineOffset: "2px",
                    }}
                    css={{
                      "& .blog-card-arrow": {
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transform: "translateX(0)",
                        transition: "transform 160ms ease",
                        lineHeight: 1,
                      },
                      "&:hover .blog-card-arrow": {
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <NextLink
                      href={`/blog/${post.slug}`}
                      onClick={() => setPageLoading(true)}
                    >
                      Continue reading
                      <Box
                        as="span"
                        className="blog-card-arrow"
                      >
                        <LuArrowRight />
                      </Box>
                    </NextLink>
                  </Button>
                </Card.Body>
              </Card.Root>
          );
        })}
      </Grid>
    </Stack>
  );
}
